/**
 * 361 (#980): the trip answers on the booking form. The platform requires them on a customer's ordinary booking
 * (`backend/src/modules/bookings/trip.ts` holds the same six purposes); an extension copies its parent's, so the form
 * does not ask then.
 */
export const TRIP_PURPOSES = [
  'laundry',
  'meal_with_friends',
  'grocery_shopping',
  'cafe_hopping',
  'uni_project',
  'entertainment',
] as const;
export type TripPurpose = (typeof TRIP_PURPOSES)[number];

export const TRIP_PURPOSE_LABELS: Record<TripPurpose, string> = {
  laundry: 'Laundry',
  meal_with_friends: 'Meal with friends',
  grocery_shopping: 'Grocery shopping',
  cafe_hopping: 'Cafe hopping',
  uni_project: 'Uni project',
  entertainment: 'Entertainment',
};

/** What the customer has entered so far. The number stays text: it is typed, and a wrong entry must stay visible. */
export interface TripDraft {
  students: string;
  purpose: TripPurpose | '';
  foodDelivery: boolean | null;
  nonHalal: boolean | null;
}

/** The answers as `POST /bookings` takes them. */
export interface Trip {
  students: number;
  purpose: TripPurpose;
  food_delivery: boolean;
  non_halal?: boolean;
}

export const EMPTY_TRIP_DRAFT: TripDraft = {
  students: '',
  purpose: '',
  foodDelivery: null,
  nonHalal: null,
};

/** A whole number from 1 to 5, or null. The PO wants it typed, not picked, so most people do not just pick 1. */
export function studentsValue(text: string): number | null {
  const m = /^\s*([1-5])\s*$/.exec(text);
  return m ? Number(m[1]) : null;
}

/** The trip to send, or null while anything is missing or invalid. */
export function tripFromDraft(d: TripDraft): Trip | null {
  const students = studentsValue(d.students);
  if (students === null || d.purpose === '' || d.foodDelivery === null) return null;
  if (!d.foodDelivery) return { students, purpose: d.purpose, food_delivery: false };
  if (d.nonHalal === null) return null;
  return { students, purpose: d.purpose, food_delivery: true, non_halal: d.nonHalal };
}

/** The line under a disabled Continue (design 2b), or null when every question is answered. */
export function tripBlockedSentence(d: TripDraft): string | null {
  const missing: string[] = [];
  if (studentsValue(d.students) === null) missing.push('the number of students (1–5)');
  if (d.purpose === '') missing.push("the trip's purpose");
  if (d.foodDelivery === null) missing.push('whether you are OK to do food delivery');
  else if (d.foodDelivery && d.nonHalal === null) missing.push('whether non-halal food is OK');
  if (missing.length === 0) return null;
  const list =
    missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')}, and ${missing[missing.length - 1]}`;
  return `To continue: ${list}.`;
}
